import { parseJson, toJson, toJsonObject, toString } from '@tryiris-ai/js-types';
import { AuthenticateWithIdToken } from '@tryiris-ai/mailroom-helper/auth';
import { connect } from 'nats.ws';
import type { ConnectionOptions } from 'nats.ws';
import { createUser } from 'nkeys.js';
import { NatsUserKey } from './constants.js';
import type { NatsToken, NatsUser } from './types.js';
import { toApiError } from '#~/api/index.js';
import { logger } from '#~/logger/index.js';
import * as localStorage from '#~/persistence/index.js';

const createNatsUser = (): NatsUser => {
  const nkUser = createUser();

  const natsUser = {
    id: nkUser.getPublicKey(),
    seed: new TextDecoder().decode(nkUser.getSeed())
  };

  localStorage.set(NatsUserKey, toJson(natsUser));

  return natsUser;
};

const getNatsUser = async (): Promise<NatsUser> => {
  const user = await localStorage.get(NatsUserKey);

  const candidate = toJsonObject<Partial<NatsUser>>(parseJson(user));

  const id = toString(candidate?.id);
  const seed = toString(candidate?.seed);

  if (id && seed) {
    return { id, seed };
  }

  return createNatsUser();
};

/**
 * Gets the NATS user from local storage or creates a new one.
 *
 * @param accessToken - The access token to authenticate with.
 * @param options - The NATS connection options.
 * @returns The NATS token.
 */
export const getNatsToken = async (
  accessToken: string,
  options: ConnectionOptions
): Promise<NatsToken> => {
  const tokenExpAt = await localStorage.get('tokenExpiresAt');
  const { id, seed } = await getNatsUser();

  if (tokenExpAt && Number(tokenExpAt) > Date.now() / 1000) {
    const storedToken = await localStorage.get('token');

    if (storedToken) {
      return { seed, token: storedToken };
    }
  }

  let connection;

  try {
    connection = await connect(options);

    const resp = await AuthenticateWithIdToken.request(connection, {
      authenticationType: 0,
      natsUserId: id,
      token: accessToken
    });

    if (resp.error) {
      throw toApiError(resp.error);
    }

    await localStorage.set('token', resp.token);
    await localStorage.set('tokenExpiresAt', resp.expiresAt.toString());

    return { seed, token: resp.token };
  } finally {
    connection?.close().catch((error) => logger.critical(error));
  }
};
